import { ItineraryActivity } from "./activity.model";

/**
 * Rappresenta un singolo giorno nell'itinerario con metadata
 */
export class ItineraryDayBuilder {
  date: string; // YYYY-MM-DD
  activities: ItineraryActivity[] = [];
  
  // Flags per analisi del giorno
  hasCheckIn: boolean = false;
  hasCheckOut: boolean = false;
  isWithinAccommodation: boolean = false; // Giorno coperto da alloggio ma senza check-in/out
  isFreeDay: boolean = false; // Solo trasporti/alloggi, nessuna attività
  hasFlights: boolean = false;
  hasTransportation: boolean = false;
  hasActivities: boolean = false; // Attività turistiche effettive
  accommodationId: string | null = null; // ID dell'alloggio che copre questo giorno
  
  constructor(date: string, activities: ItineraryActivity[] = []) {
    this.date = date;
    this.activities = activities;
    this.updateMetadata();
  }

  /**
   * Aggiorna i metadata del giorno analizzando le attività
   */
  updateMetadata(): void {
    this.hasCheckIn = false;
    this.hasCheckOut = false;
    this.hasFlights = false;
    this.hasTransportation = false;
    this.hasActivities = false;
    this.accommodationId = null;

    for (const activity of this.activities) {
      // Check accommodation
      if (activity.type === 'accommodation') {
        if (activity.inOrOutActivity === 'in') {
          this.hasCheckIn = true;
          this.accommodationId = activity.id;
        } else if (activity.inOrOutActivity === 'out') {
          this.hasCheckOut = true;
        }
      }

      // Check transportation types
      if (activity.type === 'flight') {
        this.hasFlights = true;
      } else if (activity.type === 'transportation') {
        this.hasTransportation = true;
      }

      // Check actual activities (things to do)
      if (activity.type === 'activity' || activity.type === 'events' || activity.type === 'food-drinks') {
        this.hasActivities = true;
      }
    }

    // Determina se è un giorno libero
    this.isFreeDay = !this.hasActivities && 
                      !this.hasCheckIn && 
                      !this.hasCheckOut && 
                      this.activities.length > 0;
  }

  /**
   * Aggiunge un'attività al giorno
   */
  addActivity(activity: ItineraryActivity): void {
    activity.location = String(this.activities.length);
    this.activities.push(activity);
    this.updateMetadata();
  }

  /**
   * Rimuove un'attività dal giorno tramite ID
   */
  removeActivity(activityId: string): boolean {
    const initialLength = this.activities.length;
    this.activities = this.activities.filter(act => act.id !== activityId);
    const removed = this.activities.length < initialLength;
    
    if (removed) {
      this.updateMetadata();
    }
    
    return removed;
  }

  /**
   * Trova un'attività tramite ID
   */
  findActivity(activityId: string): ItineraryActivity | undefined {
    return this.activities.find(act => act.id === activityId);
  }

  /**
   * Sostituisce un'attività esistente
   */
  updateActivity(activityId: string, newActivity: ItineraryActivity): boolean {
    const index = this.activities.findIndex(act => act.id === activityId);
    if (index !== -1) {
      this.activities[index] = newActivity;
      this.updateMetadata();
      return true;
    }
    return false;
  }

  /**
   * Restituisce un summary del giorno
   */
  getSummary(): string {
    if (this.activities.length === 0) return 'Empty day';
    
    const parts: string[] = [];
    if (this.hasCheckIn) parts.push('Check-in');
    if (this.hasCheckOut) parts.push('Check-out');
    if (this.hasFlights) parts.push('Flight');
    if (this.hasTransportation) parts.push('Transport');
    if (this.hasActivities) parts.push(`${this.activities.filter(a => a.type === 'activity' || a.type === 'events').length} activities`);
    
    return parts.join(' • ') || 'Day with activities';
  }
}

/**
 * Classe principale per gestire l'intero itinerario
 */
export class ItineraryBuilder {
  private days: Map<string, ItineraryDayBuilder> = new Map();
  startDate: string | null = null;
  endDate: string | null = null;

  constructor(startDate?: string, endDate?: string) {
    if (startDate && endDate) {
      this.initializeDays(startDate, endDate);
    }
  }

  /**
   * Inizializza tutti i giorni tra startDate e endDate
   */
  initializeDays(startDate: string, endDate: string): void {
    this.startDate = startDate;
    this.endDate = endDate;
    this.days.clear();

    const start = new Date(startDate);
    const end = new Date(endDate);
    
    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      const dateStr = date.toISOString().split('T')[0];
      this.days.set(dateStr, new ItineraryDayBuilder(dateStr));
    }
  }

  /**
   * Carica giorni da un array esistente
   * Se startDate/endDate sono forniti, genera tutti i giorni nell'intervallo
   * altrimenti usa solo i giorni presenti nei dati
   */
  loadFromArray(daysData: { date: string; activities: ItineraryActivity[] }[], startDate?: string, endDate?: string): void {
    console.log('🔧 ItineraryBuilder.loadFromArray called:', {
      daysDataCount: daysData.length,
      startDate,
      endDate,
      daysWithActivities: daysData.map(d => d.date)
    });
    
    this.days.clear();
    
    // Se abbiamo startDate e endDate, genera tutti i giorni dell'intervallo
    if (startDate && endDate) {
      console.log('✅ Initializing all days from', startDate, 'to', endDate);
      this.initializeDays(startDate, endDate);
      console.log('📅 Total days created:', this.days.size, 'Days:', Array.from(this.days.keys()));
      
      // Poi popola i giorni con le attività esistenti
      for (const dayData of daysData) {
        const day = this.days.get(dayData.date);
        if (day) {
          // Rimuovi attività esistenti e aggiungi quelle nuove
          day.activities = dayData.activities as ItineraryActivity[];
          day.updateMetadata();
        }
      }
    } else {
      console.warn('⚠️ No startDate/endDate provided, loading only days with activities');
      // Fallback: importa solo i giorni con attività
      for (const dayData of daysData) {
        const day = new ItineraryDayBuilder(dayData.date, dayData.activities as ItineraryActivity[]);
        this.days.set(dayData.date, day);
      }

      // Aggiorna date range dai giorni caricati
      const dates = Array.from(this.days.keys()).sort();
      if (dates.length > 0) {
        this.startDate = dates[0];
        this.endDate = dates[dates.length - 1];
      }
    }

    // Analizza accommodation coverage
    this.updateAccommodationCoverage();
  }

  /**
   * Aggiunge un'attività a un giorno specifico
   */
  addActivity(date: string, activity: ItineraryActivity): void {
    let day = this.days.get(date);
    
    // Se il giorno non esiste, crealo
    if (!day) {
      console.warn('⚠️ addActivity: Creating day manually for', date, 'Activity:', activity.type, activity.title);
      day = new ItineraryDayBuilder(date);
      this.days.set(date, day);
    } else {
      console.log('✅ addActivity: Day exists for', date, 'Adding:', activity.type, activity.title);
    }
    
    day.addActivity(activity);
    
    // Se è un accommodation, aggiorna la copertura
    if (activity.type === 'accommodation') {
      this.updateAccommodationCoverage();
    }
  }

  /**
   * Rimuove un'attività da tutti i giorni (utile per accommodation con in/out)
   */
  removeActivityById(activityId: string): string[] {
    const affectedDates: string[] = [];
    
    // Rimuovi anche l'attività out associata se è un accommodation
    const idsToRemove = [activityId, `${activityId}-out`];
    
    for (const [date, day] of this.days.entries()) {
      for (const id of idsToRemove) {
        if (day.removeActivity(id)) {
          affectedDates.push(date);
        }
      }
    }
    
    // Aggiorna la copertura accommodation
    this.updateAccommodationCoverage();
    
    return affectedDates;
  }

  /**
   * Aggiorna i flag isWithinAccommodation per tutti i giorni
   */
  private updateAccommodationCoverage(): void {
    // Reset tutti i flag
    for (const day of this.days.values()) {
      day.isWithinAccommodation = false;
    }

    // Trova tutti gli accommodation
    const accommodations: { id: string; startDate: string; endDate: string }[] = [];
    
    for (const day of this.days.values()) {
      for (const activity of day.activities) {
        if (activity.type === 'accommodation' && activity.inOrOutActivity === 'in') {
          accommodations.push({
            id: activity.id,
            startDate: activity.start_date || activity.date,
            endDate: activity.end_date || activity.date
          });
        }
      }
    }

    // Marca i giorni coperti da accommodation
    for (const acc of accommodations) {
      const start = new Date(acc.startDate);
      const end = new Date(acc.endDate);
      
      for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
        const dateStr = date.toISOString().split('T')[0];
        const day = this.days.get(dateStr);
        
        if (day) {
          day.isWithinAccommodation = true;
          day.accommodationId = acc.id;
        }
      }
    }
  }

  /**
   * Ottiene un giorno specifico
   */
  getDay(date: string): ItineraryDayBuilder | undefined {
    return this.days.get(date);
  }

  /**
   * Trova un'attività ovunque e restituisce l'attività e la data a cui apparteneva
   */
  findActivityById(activityId: string): { activity: ItineraryActivity; date: string } | null {
    for (const [date, day] of this.days.entries()) {
      const found = day.findActivity(activityId);
      if (found) return { activity: found, date };
    }
    return null;
  }

  /**
   * Sposta un'attività (rimuovendola dalla sua posizione) e la inserisce prima di un'altra attività nel giorno target
   * Se targetActivityId è null, append alla fine del giorno target
   */
  moveActivityBefore(sourceActivityId: string, targetDate: string, targetActivityId?: string): boolean {
    // Trova source
    const found = this.findActivityById(sourceActivityId);
    if (!found) return false;

    const { activity } = found;

    // Rimuovi dal giorno sorgente
    const sourceDate = found.date;
    this.removeActivityById(sourceActivityId);

    // Assicurati che il giorno target esista
    let targetDay = this.days.get(targetDate);
    if (!targetDay) {
      targetDay = new ItineraryDayBuilder(targetDate);
      this.days.set(targetDate, targetDay);
    }

    if (!targetActivityId) {
      // append
      targetDay.addActivity(activity);
      // reindex activities for the target day (and source day)
      this.reindexDay(targetDate);
      if (sourceDate && sourceDate !== targetDate) this.reindexDay(sourceDate);
      // aggiorna coverage accommodation dopo lo spostamento
      this.updateAccommodationCoverage();
      return true;
    }

    // trova indice target
    const idx = targetDay.activities.findIndex(a => a.id === targetActivityId);
    if (idx === -1) {
      // se non trovato, append
      targetDay.addActivity(activity);
      return true;
    }

    // inserisci prima dell'indice
    targetDay.activities.splice(idx, 0, activity);
    targetDay.updateMetadata();

    // Reindicizza le attività del giorno target e del giorno sorgente (se diverso)
    this.reindexDay(targetDate);
    if (sourceDate && sourceDate !== targetDate) this.reindexDay(sourceDate);

    // aggiorna coverage accommodation dopo lo spostamento
    this.updateAccommodationCoverage();

    return true;
  }

  /**
   * Imposta il campo `location` (stringa) per tutte le attività del giorno
   * in base alla loro posizione nell'array (0..n)
   */
  private reindexDay(date: string): void {
    const day = this.days.get(date);
    if (!day) return;
    for (let i = 0; i < day.activities.length; i++) {
      try {
        // salvo come stringa per mantenere compatibilità con DB/text fields
        (day.activities[i] as any).location = String(i);
      } catch (e) {
        // ignore
      }
    }
    day.updateMetadata();
  }

  /**
   * Restituisce tutti i giorni ordinati per data
   */
  getAllDays(): ItineraryDayBuilder[] {
    return Array.from(this.days.values()).sort((a, b) => 
      a.date.localeCompare(b.date)
    );
  }

  /**
   * Restituisce i giorni come array semplice per il salvataggio
   */
  toArray(): { date: string; activities: ItineraryActivity[] }[] {
    return this.getAllDays().map(day => ({
      date: day.date,
      activities: day.activities as ItineraryActivity[]
    }));
  }

  /**
   * Restituisce solo i giorni con attività per il salvataggio (esclude giorni vuoti)
   */
  toSaveArray(): { date: string; activities: ItineraryActivity[] }[] {
    return this.getAllDays()
      .filter(day => day.activities.length > 0)
      .map(day => ({
        date: day.date,
        activities: day.activities.map(act => {
          // Rimuovi i campi computed che non vanno salvati
          const { inOrOutActivity, is_multi_day, isMultiDay, ...activityData } = act as any;
          return activityData;
        })
      }));
  }

  /**
   * Analisi dell'itinerario: giorni senza alloggio
   */
  getDaysWithoutAccommodation(): string[] {
    const days: string[] = [];
    
    for (const day of this.days.values()) {
      // Escludi primo e ultimo giorno (potrebbero essere solo trasferimenti)
      if (day.date === this.startDate || day.date === this.endDate) continue;
      
      if (!day.isWithinAccommodation && !day.hasCheckIn) {
        days.push(day.date);
      }
    }
    
    return days;
  }

  /**
   * Analisi: giorni completamente vuoti
   */
  getEmptyDays(): string[] {
    return this.getAllDays()
      .filter(day => day.activities.length === 0)
      .map(day => day.date);
  }

  /**
   * Analisi: giorni con solo trasporti (possibili giorni di transito)
   */
  getTransitDays(): string[] {
    return this.getAllDays()
      .filter(day => day.isFreeDay)
      .map(day => day.date);
  }

  /**
   * Conta attività per tipo, escludendo le 'out'
   */
  getActivityStats(): Record<string, number> {
    const stats: Record<string, number> = {};
    for (const day of this.days.values()) {
      for (const activity of day.activities) {
        if (activity.inOrOutActivity === 'out') continue;
        stats[activity.type] = (stats[activity.type] || 0) + 1;
      }
    }
    return stats;
  }

  /**
   * Verifica se l'itinerario è valido (ha almeno un giorno con attività)
   */
  isValid(): boolean {
    return this.getAllDays().some(day => day.activities.length > 0);
  }

  /**
   * Ottiene un report completo dell'itinerario
   */
  getReport(): {
    totalDays: number;
    daysWithActivities: number;
    emptyDays: number;
    daysWithoutAccommodation: string[];
    transitDays: string[];
    activityStats: Record<string, number>;
    isValid: boolean;
  } {
    return {
      totalDays: this.days.size,
      daysWithActivities: this.getAllDays().filter(d => d.activities.length > 0).length,
      emptyDays: this.getEmptyDays().length,
      daysWithoutAccommodation: this.getDaysWithoutAccommodation(),
      transitDays: this.getTransitDays(),
      activityStats: this.getActivityStats(),
      isValid: this.isValid()
    };
  }
}
